import { Box, Divider, Grid, Stack } from '@mui/material'
import { memo } from 'react'
import { RangeBarSkeleton } from './RangeBar'

export type SecurityOverviewSkeletonProps = {
    /** Hold the 250px chart slot open; the chart paints its own shimmer once it mounts, so the slot stays blank. */
    showChart?: boolean
    /** Index securities lead with the components table before the field groups. */
    isIndex?: boolean
}

const SECTION_TAB_WIDTHS = [78, 88, 96, 72, 80, 84]
const COMMUNITY_CHIP_WIDTHS = [96, 124, 84, 108, 40]
const FIELD_GROUP_COUNT = 2
const FIELDS_PER_GROUP = 8
const INDEX_COMPONENT_ROWS = 6

/** Mirrors OverviewSectionHeader: divider, then a 14px uppercase label with 8px/16px padding. */
const SectionHeader = ({ width }: { width: number }) => (
    <Box>
        <Divider />
        <Box sx={{ px: 2, height: 37, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width, height: 14 }} />
        </Box>
    </Box>
)

/** Mirrors SecurityOverviewHeader: avatar, symbol, company name, and the two 32px action buttons. */
const Header = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, minHeight: 40 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0 }} />
            <Stack direction="row" gap={1} pl={1} alignItems="center">
                <Box sx={{ width: 64, height: 14 }} />
                <Box sx={{ width: 140, height: 14 }} />
            </Stack>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Box sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '4px' }} />
            </Box>
            <Box sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '4px' }} />
            </Box>
        </Box>
    </Box>
)

/** Mirrors OverviewSectionTabs: a row of 24px chips with 8px/16px padding over a divider. */
const SectionTabs = () => (
    <Box>
        <Box sx={{ display: 'flex', gap: 0.75, px: 2, py: 1, overflow: 'hidden' }}>
            {SECTION_TAB_WIDTHS.map((width, index) => (
                <Box key={index} sx={{ width, height: 24, borderRadius: '4px', flexShrink: 0 }} />
            ))}
        </Box>
        <Divider />
    </Box>
)

/** Mirrors IndexComponentsSection: 32px table rows plus the "show all" button. */
const IndexComponents = () => (
    <Box>
        {Array.from({ length: INDEX_COMPONENT_ROWS }).map((_, index) => (
            <Box key={index} sx={{ height: 32, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0 }} />
                <Box sx={{ width: 88, height: 14 }} />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 44, height: 14 }} />
                <Box sx={{ width: 40, height: 14 }} />
                <Box sx={{ width: 48, height: 14 }} />
            </Box>
        ))}
        <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ mt: 1.5, height: 32, borderRadius: '4px' }} />
        </Box>
    </Box>
)

/** Mirrors OverviewPerformanceSection: six period tiles, then the day and 52-week range bars. */
const Performance = () => (
    <Box>
        <SectionHeader width={92} />
        <Box sx={{ px: 2, py: 1 }}>
            <Grid container spacing={1}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <Grid key={index} item xs={4}>
                        <Box sx={{ height: 52, borderRadius: '4px' }} />
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ mt: 2 }}>
                <RangeBarSkeleton show52WkRange />
            </Box>
        </Box>
    </Box>
)

/** Mirrors FieldGroupGrid: two columns of caption label over body2 value. */
const FieldGroup = ({ labelWidth }: { labelWidth: number }) => (
    <Box>
        <SectionHeader width={labelWidth} />
        <Grid container rowSpacing={1.75} sx={{ p: 2 }}>
            {Array.from({ length: FIELDS_PER_GROUP }).map((_, index) => (
                <Grid key={index} item xs={6}>
                    <Stack>
                        {/* 10px caption at line-height 1.66, then a body2 line: matches FieldGroupGrid's cell height. */}
                        <Box sx={{ height: 16.6, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 64 + (index % 3) * 12, height: 10 }} />
                        </Box>
                        <Box sx={{ height: 20, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 80 + (index % 2) * 24, height: 14 }} />
                        </Box>
                    </Stack>
                </Grid>
            ))}
        </Grid>
    </Box>
)

/** Mirrors CommunityScreenChips: wrapped 24px chips under the section header. */
const CommunityScreens = () => (
    <Box>
        <SectionHeader width={132} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 2, pb: 2 }}>
            {COMMUNITY_CHIP_WIDTHS.map((width, index) => (
                <Box key={index} sx={{ width, height: 24, borderRadius: '4px' }} />
            ))}
        </Box>
    </Box>
)

/** Mirrors the Seasonals / Technicals sections in their own loading state: header plus one rounded block. */
const BlockSection = ({ labelWidth, height }: { labelWidth: number; height: number }) => (
    <Box>
        <SectionHeader width={labelWidth} />
        <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ height, borderRadius: '4px' }} />
        </Box>
    </Box>
)

/**
 * Structure-only placeholder for the security overview, painted by SubtleShimmer.
 * Each block keeps the real component's box model so nothing moves when content lands.
 */
function SecurityOverviewSkeleton({ showChart = false, isIndex = false }: SecurityOverviewSkeletonProps) {
    return (
        <Box sx={{ position: 'relative', display: 'flex', flex: 1, height: '100%', minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
            <Header />
            <Divider />
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {showChart && <Box data-shimmer-ignore sx={{ height: 250, flexShrink: 0 }} />}
                <SectionTabs />
                {isIndex && <IndexComponents />}
                <Performance />
                {Array.from({ length: FIELD_GROUP_COUNT }).map((_, index) => (
                    <FieldGroup key={index} labelWidth={108 + index * 16} />
                ))}
                <CommunityScreens />
                <BlockSection labelWidth={84} height={200} />
                <BlockSection labelWidth={88} height={160} />
            </Box>
        </Box>
    )
}

export default memo(SecurityOverviewSkeleton)
